/**
 * PageHeader
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectContent, openMenu, closeMenu } from 'store/modules/model';
import { useLocation } from 'react-router';
import LangButton from 'components/LangButton';
import Menu from 'components/Menu';
import HeaderLogo from 'components/PageHeader/HeaderLogo';
import Alert from 'components/Icon';
import Icon from 'components/Icon';
import { Link } from 'types/global';
import { ModelHeader } from 'types/epi';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';

interface Props {
	/** Pass an URL if the logo should be linked  */
	logoLink?: string;

	/** Logo image subtitle */
	logoSubtitle?: string;

	/** Language options */
	lang: Array<Link>;

	menu: any;
}

const menuBtnClass =
	'flex items-center justify-center px-2 mr-1 md:mr-0 min-w-menu-btn h-10 bg-grey-light hover:bg-gray-500 hover:shadow-md focus:outline-primary outline-reverse-offset-1 rounded-md uppercase bg-grey';

/** The page header component. */
const PageHeader: React.FC<ModelHeader> = (header) => {
	const pageContent = useSelector(selectContent);
	const location = useLocation();
	const [menuIsOpen, setMenuIsOpen] = React.useState(false);
	const [showInfoBox, setShowInfoBox] = useState(false);

	/** this bool allow to know if we are in mobile device or not */
	const isMobile = useMediaQuery({
		maxWidth: 768,
	});

	const dispatch = useDispatch();

	const { logo, lang, menu } = header;

	useEffect(() => {
		// This is to update epi quicknavigator link with current page id
		var epiNavigator: HTMLAnchorElement | null = document.querySelector(
			'#epi-quickNavigator > li:first-of-type > a:first-of-type'
		);
		var epiNavigatorDropDownCMSEdit: HTMLAnchorElement | null = document.querySelector(
			'#epi-quickNavigator > li:nth-of-type(2) > #epi-quickNavigator-menu > li:nth-of-type(2)> a:first-of-type'
		);

		if (epiNavigator && pageContent && epiNavigator.href) {
			const prefix = ':///';
			const pos = epiNavigator.href.lastIndexOf(prefix);
			if (pos !== -1) {
				epiNavigator.href =
					epiNavigator.href.substring(0, pos) + prefix + pageContent.id;
			}
		}

		if (
			epiNavigatorDropDownCMSEdit &&
			pageContent &&
			epiNavigatorDropDownCMSEdit.href
		) {
			const prefix = ':///';
			const pos = epiNavigatorDropDownCMSEdit.href.lastIndexOf(prefix);
			if (pos !== -1) {
				epiNavigatorDropDownCMSEdit.href =
					epiNavigatorDropDownCMSEdit.href.substring(0, pos) +
					prefix +
					pageContent.id;
			}
		}
	}, [location, pageContent]);

	useEffect(() => {
		if (isMobile) {
			if (menuIsOpen) {
				dispatch(openMenu());
			} else {
				dispatch(closeMenu());
			}
		}
	}, [menuIsOpen]);

	return (
		<>
			{header.showGlobalMessage && header.globalMessageHeadLine && (
				<div className="w-full bg-[#E2EBF0] py-4">
					<div className="max-w-4xl mx-auto px-4 text-left">
						<div className="flex items-center gap-2 align-middle">
							<Icon
								icon="alert"
								aria-hidden="true"
								className="text-red-600 flex-shrink-0 w-[22px] h-[32px]"
							/>
							<span
								className="font-roboto font-semibold text-[18px] leading-[24px] tracking-[0%] align-middle text-[#000]"
							>
								{header.globalMessageHeadLine}
							</span>
						</div>

						<div className="mt-2">
							<button
								onClick={() => setShowInfoBox((prev) => !prev)}
								className="bg-[#333333] text-white rounded-lg h-10 px-6 shadow-md hover:bg-gray-700 transition flex items-center justify-center gap-2"
							>
								{showInfoBox
									? header.globalMessageShowLessText
									: header.globalMessageShowMoreText}

								<Icon
									icon="arrow"
									color="#FFFFFF"
									direction={showInfoBox ? "up" : "down"}
									className="text-white"
								/>
							</button>
						</div>
					</div>
				</div>
			)}

			{showInfoBox && (
				<div className="w-full bg-[#E2EBF0] border-t border-gray-300 py-6">
					<div className="max-w-4xl mx-auto px-4 text-left">
						<p className="font-roboto text-[18px] leading-[24px] tracking-[0%] text-[#000]">
							{header.globalMessageText}
						</p>
					</div>
				</div>
			)}

			<header
				className="bg-white border-b-1 border-grey lg:shadow-md"
				tabIndex={-1}
			>
				<div className="main-w-container h-menu bg-white mx-auto responsive-padding header-page-padding flex items-center justify-between">
					{logo && (
						<HeaderLogo
							linkTo={logo.url}
							subtitle={logo.subtitle}
							ariaLabel={logo.ariaLabel}
						/>
					)}
					<div className="flex items-center gap-4 sm:gap-6 relative">
						{!isMobile &&
							lang?.items.map((item, index) => (
								<LangButton
									key={item.text + index.toString()}
									item={item}
									setMenuIsOpen={setMenuIsOpen}
									isMobile={isMobile}
								/>
							))}
						<button
							className={clsx(menuBtnClass, 'custom-border')}
							onClick={() => setMenuIsOpen(true)}
							aria-expanded={menuIsOpen}
							aria-label={
								menu?.closeText?.startsWith('Stäng')
									? 'Öppna meny'
									: 'Close menu'
							}
						>
							<Icon size={2} icon="menu" aria-hidden="true" />
						</button>
					</div>
				</div>
			</header>

			<Menu
				isOpen={menuIsOpen}
				setMenuIsOpen={setMenuIsOpen}
				menu={menu}
				lang={lang.items}
				langBtnStyle={menuBtnClass}
				logoLink={logo?.url}
				logoSubtitle={logo?.subtitle}
				logoAriaLabel={logo?.ariaLabel}
				isMobile={isMobile}
			/>
		</>
	);
};

export default PageHeader;